import { useQuery, useMutation, useQueryClient } from '../lib/queryClient';
import { toast } from 'sonner';
import { getAnnotationsList } from '../endpoints/annotations/list_GET.schema';
import { createAnnotation } from '../endpoints/annotations/create_POST.schema';
import { deleteAnnotation } from '../endpoints/annotations/delete_POST.schema';

export const useAnnotations = (
  sessionId: string | undefined,
  options?: { attachmentId?: string; checkpointId?: string },
) => {
  return useQuery({
    queryKey: ['annotations', 'list', sessionId, options?.attachmentId, options?.checkpointId],
    queryFn: () => {
      if (!sessionId) throw new Error('Session ID is required');
      return getAnnotationsList({
        sessionId,
        attachmentId: options?.attachmentId,
        checkpointId: options?.checkpointId,
      });
    },
    enabled: !!sessionId,
    retry: 1,
  });
};

export const useCreateAnnotation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: createAnnotation,
    onSuccess: (data) => {
      toast.success('Annotation added');
      // Invalidate queries that match the session and potentially specific attachment/checkpoint
      queryClient.invalidateQueries({
        queryKey: ['annotations', 'list', data.annotation.sessionId],
      });
    },
    onError: (error) => {
      toast.error(`Failed to add annotation: ${error.message}`);
    },
  });
};

export const useDeleteAnnotation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: deleteAnnotation,
    onSuccess: () => {
      toast.success('Annotation deleted');
      // Since we don't get sessionId back from delete, we invalidate all annotation lists.
      // This is a safe default.
      queryClient.invalidateQueries({ queryKey: ['annotations', 'list'] });
    },
    onError: (error) => {
      toast.error(`Failed to delete annotation: ${error.message}`);
    },
  });
};
